/**
 * Download the Smart Report as a single unified PDF with the lab's cover page
 * prepended as page 1.
 *
 * Strategy:
 *   1. Render each `.pdf-page` div (already sized to A4/Letter/Legal by PrintDocument)
 *      into a canvas via html2canvas at high DPI.
 *   2. Assemble into a jsPDF instance with the exact page dimensions in mm.
 *   3. Fetch the lab's cover PDF, load both PDFs with pdf-lib, prepend the cover
 *      pages, and trigger a single-file download.
 */

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";

const PAGE_SIZES = {
  A4: { w: 210, h: 297 },
  Letter: { w: 216, h: 279 },
  Legal: { w: 216, h: 356 },
};

const MM_PER_INCH = 25.4;
const PX_PER_INCH = 96;
const MM_TO_PX = PX_PER_INCH / MM_PER_INCH;

/**
 * @param {HTMLElement} docEl — the <PrintDocument> root (contains one or more .pdf-page divs).
 * @param {{page_size?: string}} letterhead — lab letterhead config (for page size).
 * @param {string} coverAbsoluteUrl — fully qualified URL to the cover PDF.
 * @param {string} filename — output filename without extension.
 */
export async function downloadReportWithCover({
  docEl,
  letterhead,
  coverAbsoluteUrl,
  filename,
}) {
  if (!docEl) throw new Error("Print document not ready");
  const pageEls = Array.from(docEl.querySelectorAll(".pdf-page"));
  if (!pageEls.length) throw new Error("No pages to render");

  const size = PAGE_SIZES[letterhead?.page_size] || PAGE_SIZES.A4;
  const pageW = size.w;
  const pageH = size.h;
  const pageWpx = pageW * MM_TO_PX;
  const pageHpx = pageH * MM_TO_PX;

  const pdf = new jsPDF({
    unit: "mm",
    format: [pageW, pageH],
    orientation: pageW > pageH ? "landscape" : "portrait",
    compress: true,
  });

  for (let i = 0; i < pageEls.length; i++) {
    const el = pageEls[i];
    // Render at 1.5x DPI — fast, still crisp text
    const canvas = await html2canvas(el, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 8000,
      foreignObjectRendering: false,
      width: pageWpx,
      height: pageHpx,
      windowWidth: pageWpx,
      windowHeight: pageHpx,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.88);
    if (i > 0) pdf.addPage([pageW, pageH], pageW > pageH ? "landscape" : "portrait");
    pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH, undefined, "FAST");
  }

  const reportPdfBytes = pdf.output("arraybuffer");

  // Fetch the cover PDF
  const coverResp = await fetch(coverAbsoluteUrl, { credentials: "omit" });
  if (!coverResp.ok) throw new Error(`Cover fetch failed (${coverResp.status})`);
  const coverBytes = await coverResp.arrayBuffer();

  // Merge with pdf-lib — cover pages first, then report pages
  const coverDoc = await PDFDocument.load(coverBytes);
  const reportDoc = await PDFDocument.load(reportPdfBytes);
  const merged = await PDFDocument.create();

  const copiedCover = await merged.copyPages(coverDoc, coverDoc.getPageIndices());
  copiedCover.forEach((p) => merged.addPage(p));

  const copiedReport = await merged.copyPages(reportDoc, reportDoc.getPageIndices());
  copiedReport.forEach((p) => merged.addPage(p));

  const finalBytes = await merged.save();

  // Trigger download
  const blob = new Blob([finalBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename || "SmartReport"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
